"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Link from "next/link";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useEffect, useRef, useState } from "react";
import getApiClient from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useBlockableRouter } from "@/components/navigation-block";
import { useTranslations } from "next-intl";
import { ApiError, translateApiError } from "@/lib/api/error";
import { FieldErrors } from "@/components/ui/field-error";
import { useApiFieldErrors } from "@/lib/api/use-field-errors";
import { TwoFactorChallenge } from "./two-factor-challenge";
import type { components } from "@/lib/api.gen";
type SecondFactorMethod = components["schemas"]["SecondFactorMethod"];
interface AuthenticationFormProps {
  authType: "login" | "signup";
  onToggleMethod: () => void;
  next: string;
  onAuthSuccess?: () => void | Promise<void>;
  signupMode?: "default" | "promote";
  promoteAfterLogin?: boolean;
  inDialog?: boolean;
}
export function AuthenticationForm({
  authType,
  onToggleMethod,
  next,
  onAuthSuccess,
  signupMode = "default",
  inDialog = false
}: AuthenticationFormProps) {
  const t = useTranslations();
  const apiClient = getApiClient();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fe = useApiFieldErrors();
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);
  const [emailLoginStatus, setEmailLoginStatus] = useState<"idle" | "waiting" | "done" | "error" | null>(null);
  const [emailLoginError, setEmailLoginError] = useState<string | null>(null);
  const [emailLoginPassphrase, setEmailLoginPassphrase] = useState<number[] | null>(null);
  const [twoFactorMethods, setTwoFactorMethods] = useState<SecondFactorMethod[] | null>(null);
  const emailLoginEventsRef = useRef<EventSource | null>(null);
  const router = useBlockableRouter();

  // Which SSO providers are configured on the backend (empty => hide the block).
  const {
    data: oauthProviders
  } = useQuery({
    queryKey: ["oauth-providers"],
    queryFn: async () => {
      const {
        data
      } = await apiClient.GET("/api/user/login/oauth/providers");
      return data?.providers ?? [];
    },
    staleTime: 5 * 60 * 1000
  });
  const startOauthLogin = (provider: string) => {
    // Full-page navigation: the backend 302-redirects on to the provider and,
    // after the callback, back into the app. `next` is preserved across the trip.
    window.location.href = `/api/user/login/oauth/${provider}/start?next=${encodeURIComponent(next)}`;
  };
  useEffect(() => {
    return () => {
      emailLoginEventsRef.current?.close();
    };
  }, []);
  const completeAuth = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["user-summary"]
    });
    if (onAuthSuccess) {
      await onAuthSuccess();
      return;
    }
    // A fresh signup always has an incomplete profile, so it would be bounced to
    // the completion gate anyway. Send it there directly to avoid a visible hop
    // through the home route. Regular signups then continue to the "next step"
    // chooser; signups that arrived with an explicit destination (e.g. an event
    // deep link) honour that instead.
    if (authType === "signup") {
      const afterComplete = next === "/" ? "/profile/next-step" : next;
      router.push(`/profile/complete?next=${encodeURIComponent(afterComplete)}`);
      return;
    }
    router.push(next);
  };

  // Signup validation errors (email taken, invalid email, password too short, …)
  // come back as a typed `ApiError`: show specifics inline at each field and a
  // concise translated message below the form.
  const handleSignupError = (error_0: unknown) => {
    const apiError = ApiError.from(error_0);
    fe.captureError(apiError);
    setError(translateApiError(apiError, t));
    console.error(error_0);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    fe.clear();
    if (authType === "login") {
      const hasPassword = password.trim().length > 0;
      if (!hasPassword) {
        setIsLoading(false);
        handleEmailLinkLogin();
        return;
      }
      const {
        data: data_0,
        error: error_1
      } = await apiClient.POST("/api/user/login", {
        body: {
          email,
          password
        }
      });
      if (error_1) {
        setError(t("auth.login.error.invalid-credentials"));
        console.error(error_1);
      } else if (data_0?.status === "second_factor_required") {
        setTwoFactorMethods(data_0.methods);
      } else {
        await completeAuth();
      }
    } else {
      // Handle signup
      const trimmedName = name.trim();
      if (!acceptTerms) {
        fe.setFieldError("accept_terms", ["terms_not_accepted"]);
        setIsLoading(false);
        return;
      }
      if (signupMode === "promote") {
        const {
          error: error_2
        } = await apiClient.POST("/api/user/signup", {
          body: {
            name: trimmedName || email,
            email,
            password,
            accept_terms: acceptTerms,
            marketing_consent: marketingConsent
          }
        });
        if (error_2) {
          handleSignupError(error_2);
        } else {
          await completeAuth();
        }
      } else {
        const {
          error: error_3
        } = await apiClient.POST("/api/user/signup", {
          body: {
            email,
            name: trimmedName || undefined,
            password,
            accept_terms: acceptTerms,
            marketing_consent: marketingConsent
          }
        });
        if (error_3) {
          handleSignupError(error_3);
        } else {
          // Log in the user after successful signup
          const {
            error: loginError
          } = await apiClient.POST("/api/user/login", {
            body: {
              email,
              password
            }
          });
          if (loginError) {
            setError(t("auth.signup.error.login-after-signup-failed"));
          } else {
            await completeAuth();
          }
        }
      }
    }
    setIsLoading(false);
  };
  const handleEmailLinkLogin = async () => {
    setError(null);
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError(t("auth.email-login.error.no-email"));
      return;
    }
    setEmailLoginError(null);
    setEmailLoginPassphrase(null);
    setEmailLoginStatus("idle");
    try {
      const {
        data: data_1,
        error: startError
      } = await apiClient.POST("/api/user/login/email/start", {
        body: {
          email: trimmedEmail
        }
      });
      if (startError || !data_1) {
        throw new Error(t("auth.email-login.error.start-failed"));
      }
      setEmailLoginPassphrase(data_1.passphrase);
      setEmailLoginStatus("waiting");
      const base = window.location.origin;
      // Shared real-time stream; the backend emits a named `login-verified`
      // event for this session once the magic link is followed.
      const subscribeUrl = new URL("/api/sse-events", base);
      emailLoginEventsRef.current?.close();
      const events = new EventSource(subscribeUrl.toString(), {
        withCredentials: true
      });
      emailLoginEventsRef.current = events;
      events.addEventListener("login-verified", async () => {
        events.close();
        const {
          data: upgradeData,
          error: upgradeError
        } = await apiClient.POST("/api/user/login/upgrade");
        if (upgradeError) {
          setEmailLoginStatus("error");
          setEmailLoginError(t("auth.email-login.error.upgrade-failed"));
          return;
        }
        if (upgradeData?.status === "second_factor_required") {
          setEmailLoginStatus(null);
          setTwoFactorMethods(upgradeData.methods);
          return;
        }
        setEmailLoginStatus("done");
        await completeAuth();
      });
      events.onerror = () => {
        events.close();
        setEmailLoginStatus("error");
        setEmailLoginError(t("auth.email-login.error.connection-error"));
      };
    } catch (e_0) {
      setEmailLoginStatus("error");
      setEmailLoginError(e_0 instanceof Error ? e_0.message : t("auth.email-login.error.failed"));
    }
  };
  const handleOpenResetDialog = () => {
    setResetEmail(email.trim());
    setResetMessage(null);
    setResetError(null);
    setIsResetDialogOpen(true);
  };
  const handleResetDialogOpenChange = (nextOpen: boolean) => {
    setIsResetDialogOpen(nextOpen);
  };
  const handleResetPasswordRequest = async () => {
    const trimmedEmail_0 = resetEmail.trim();
    if (!trimmedEmail_0) {
      setResetError(t("auth.reset.error.no-email"));
      return;
    }
    setIsResetLoading(true);
    setResetError(null);
    setResetMessage(null);
    const {
      error: error_4
    } = await apiClient.POST("/api/user/password/reset", {
      body: {
        email: trimmedEmail_0
      }
    });
    if (error_4) {
      setResetError(t("auth.reset.error.send-failed"));
      setIsResetLoading(false);
      return;
    }
    setResetMessage(t("auth.reset.success"));
    setIsResetLoading(false);
  };
  const isLogin = authType === "login";
  const hasPassword_0 = password.trim().length > 0;
  const emailLoginWords = t.raw("auth.email-login.words") as string[];
  const isEmailLoginPending = isLogin && (emailLoginStatus === "idle" || emailLoginStatus === "waiting");
  const areFormControlsDisabled = isLoading || isEmailLoginPending;
  const isDirty = isLogin ? email.trim().length > 0 || password.length > 0 : name.trim().length > 0 || email.trim().length > 0 || password.length > 0;
  const pwResetDialog = <Dialog open={isResetDialogOpen} onOpenChange={handleResetDialogOpenChange}>
      <p>
        {t("auth.login.forgot-password")}{" "}
        <DialogTrigger asChild>
          <Button type="button" variant="link" className="h-auto p-0 align-baseline text-sm font-normal underline" onClick={handleOpenResetDialog}>
            {t("auth.login.reset-password")}
          </Button>
        </DialogTrigger>
      </p>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("auth.login.reset-password")}</DialogTitle>
          <DialogDescription>
            {t("auth.reset.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label htmlFor="reset-email">{t("auth.field.email")}</Label>
          <Input id="reset-email" type="email" value={resetEmail} onChange={e_1 => setResetEmail(e_1.target.value)} disabled={isResetLoading} />
        </div>
        {resetError && <p className="text-destructive text-sm">{resetError}</p>}
        {resetMessage && <p className="text-sm text-green-600">{resetMessage}</p>}
        <DialogFooter>
          <Button type="button" onClick={handleResetPasswordRequest} disabled={isResetLoading}>
            {isResetLoading ? t("auth.reset.sending") : t("auth.reset.send-link")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
  const content = <>
      {!isLogin && <div className="grid gap-2">
          <Label htmlFor="name">{t("auth.field.name")}</Label>
          <Input id="name" type="text" placeholder="Max Mustermann" value={name} onChange={e_2 => {
        setName(e_2.target.value);
        fe.clearField("name");
      }} aria-invalid={!!fe.fieldErrors["name"]} disabled={areFormControlsDisabled} />
          <FieldErrors codes={fe.fieldErrors["name"]} />
        </div>}
      <div className="grid gap-2">
        <Label htmlFor="email">{t("auth.field.email")}</Label>
        <Input id="email" type="email" placeholder="m@example.com" required value={email} onChange={e_3 => {
        setEmail(e_3.target.value);
        fe.clearField("email");
      }} aria-invalid={!!fe.fieldErrors["email"]} disabled={areFormControlsDisabled} />
        <FieldErrors codes={fe.fieldErrors["email"]} />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="password">
          {t("auth.field.password")} <span className="text-xs text-muted-foreground">{t("auth.field.password-optional")}</span>
        </Label>
        <Input id="password" type="password" value={password} onChange={e_4 => {
        setPassword(e_4.target.value);
        fe.clearField("password");
      }} aria-invalid={!!fe.fieldErrors["password"]} disabled={areFormControlsDisabled} />
        <FieldErrors codes={fe.fieldErrors["password"]} />
      </div>
      {!isLogin && <>
          <div className="grid gap-2">
            <div className="flex items-start space-x-2">
              <Checkbox id="accept-terms" checked={acceptTerms} onCheckedChange={v => {
            setAcceptTerms(v === true);
            fe.clearField("accept_terms");
          }} aria-invalid={!!fe.fieldErrors["accept_terms"]} disabled={areFormControlsDisabled} className="mt-0.5" />
              <Label htmlFor="accept-terms" className="block text-sm font-normal leading-snug">
                {t.rich("auth.field.accept-terms", {
              privacy: chunks => <Link href="/privacy-policy" target="_blank" className="underline">
                      {chunks}
                    </Link>,
              agb: chunks_0 => <Link href="/agb" target="_blank" className="underline">
                      {chunks_0}
                    </Link>
            })}
              </Label>
            </div>
            <FieldErrors codes={fe.fieldErrors["accept_terms"]} />
          </div>
          <div className="flex items-start space-x-2">
            <Checkbox id="marketing-consent" checked={marketingConsent} onCheckedChange={v_0 => setMarketingConsent(v_0 === true)} disabled={areFormControlsDisabled} className="mt-0.5" />
            <Label htmlFor="marketing-consent" className="block text-sm font-normal leading-snug">
              {t("auth.field.marketing-consent")}
            </Label>
          </div>
        </>}
      <div className="mt-4 text-center text-sm">
        {isLogin ? <>
            {pwResetDialog}
            <p className="mt-1">
              {t("auth.login.no-account")}{" "}
              <Button type="button" variant="link" className="h-auto p-0 align-baseline text-sm font-normal underline" onClick={onToggleMethod}>
                {t("auth.signup.action")}
              </Button>
            </p>
          </> : <>
            {t("auth.signup.have-account")}{" "}
            <Button type="button" variant="link" className="h-auto p-0 align-baseline text-sm font-normal underline" onClick={onToggleMethod}>
              {t("auth.login.action")}
            </Button>
          </>}
      </div>
      {error && <p className="text-destructive text-sm">{error}</p>}
    </>;
  const oauthButtons = (oauthProviders?.length ?? 0) > 0 && <div className="mt-4 w-full">
      <div className="relative mb-4 text-center">
        <span className="relative z-10 bg-card px-2 text-xs text-muted-foreground">
          {t("auth.oauth.divider")}
        </span>
        <span className="absolute inset-x-0 top-1/2 -z-0 border-t" aria-hidden />
      </div>
      <div className="grid gap-2">
        {oauthProviders?.includes("google") && <Button type="button" variant="outline" className="w-full" disabled={areFormControlsDisabled} onClick={() => startOauthLogin("google")}>
            {t("auth.oauth.google")}
          </Button>}
        {oauthProviders?.includes("apple") && <Button type="button" variant="outline" className="w-full" disabled={areFormControlsDisabled} onClick={() => startOauthLogin("apple")}>
            {t("auth.oauth.apple")}
          </Button>}
      </div>
      <p className="mt-2 text-center text-xs text-muted-foreground">
        {t("auth.oauth.terms-note")}
      </p>
    </div>;
  const footerContent = <>
    <Button type="submit" className="w-full" disabled={!isDirty || isLoading || isEmailLoginPending}>
      {isEmailLoginPending ? t("auth.email-login.pending") : isLoading ? isLogin ? hasPassword_0 ? t("auth.login.loading") : t("auth.email-login.preparing") : t("auth.signup.loading") : isLogin ? hasPassword_0 ? t("auth.login.action") : t("auth.email-login.action") : t("auth.signup.action")}
    </Button>
    {oauthButtons}
    {isLogin && emailLoginStatus !== null && <div className="mt-2 w-full text-sm">
        {emailLoginStatus === "waiting" && <>
            <p>{t("auth.email-login.check-inbox")}</p>
            {emailLoginPassphrase && <div className="mt-2 rounded-md border bg-muted/50 p-3">
                <p className="text-muted-foreground">{t("auth.email-login.passphrase-label")}</p>
                <p className="mt-1 font-mono text-base font-semibold tracking-wide" data-testid="login-passphrase">
                  {emailLoginPassphrase.map(i => emailLoginWords[i]).join("-")}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">{t("auth.email-login.passphrase-hint")}</p>
              </div>}
          </>}
        {emailLoginStatus === "done" && <p>{t("auth.email-login.confirmed")}</p>}
        {emailLoginStatus === "error" && <p className="text-destructive">{emailLoginError ?? t("auth.email-login.error.failed")}</p>}
        {emailLoginStatus === "idle" && <p>{t("auth.email-login.starting")}</p>}
      </div>}
  </>;
  const form = <form onSubmit={handleSubmit}>
      {inDialog ? <>
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {isLogin ? t("auth.login.action") : t("auth.signup.action")}
          </DialogTitle>
          <DialogDescription>
            {isLogin ? t("auth.login.description") : t("auth.signup.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 pt-2">
          {content}
        </div>
        <DialogFooter>
          <div className="flex-1 flex flex-col pt-4 justify-center">
            {footerContent}
          </div>
        </DialogFooter>
      </> : <>
        <CardHeader>
          <CardTitle className="text-2xl">
            {isLogin ? t("auth.login.action") : t("auth.signup.action")}
          </CardTitle>
          <CardDescription>
            {isLogin ? t("auth.login.description") : t("auth.signup.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 pt-2">
          {content}
        </CardContent>
        <CardFooter className="flex flex-col pt-4">
          {footerContent}
        </CardFooter>
      </>}
    </form>;

  // Once the primary factor is accepted but 2FA is required, swap the form for
  // the second-factor challenge (in the same card / dialog shell).
  if (twoFactorMethods) {
    const challenge = <TwoFactorChallenge methods={twoFactorMethods} onSuccess={completeAuth} hideHeader={inDialog} />;
    if (inDialog) {
      return <>
          <DialogHeader>
            <DialogTitle className="text-2xl">{t("auth.two-factor.title")}</DialogTitle>
          </DialogHeader>
          <div className="pt-2">{challenge}</div>
        </>;
    }
    return <Card className="w-full max-w-md">
        <CardContent className="pt-6">{challenge}</CardContent>
      </Card>;
  }
  return <>
      {inDialog ? form : <Card className="w-full max-w-md">
          {form}
        </Card>}
    </>;
}